import React, { memo } from 'react'
import type { FieldInputProps, FormikErrors, FormikProps } from 'formik'
import { ErrorMessage, Field, Form as FormikForm, Formik } from 'formik'

import { validationSchema } from '../../components/Tenants/TenantProfile/TenantLicenses/TenantLicenses.utils'
import { ReactComponent as CloseIcon } from '../../static/icons/close.svg'
import { DateField } from '../Date'
import { Select } from '../Select'

import FormActions from './FormActions/FormActions'
import type { FieldConfig, FormProps, FormValues } from './Form.types'

function Form(props: FormProps) {
  const {
    fields,
    onSubmit,
    onCancel,
    onClose,
    initialValues,
    title,
    isEditMode,
  } = props

  const initialFormValues = fields.reduce<FormValues>((acc, field) => {
    acc[field.name] =
      initialValues?.[field.name] ??
      (field.type === 'date' ? null : field.type === 'checkbox' ? false : '')

    return acc
  }, {})

  const fieldRenderers: Record<
    string,
    (field: FieldConfig, errors: FormikErrors<FormValues>) => React.ReactNode
  > = {
    select: (field) => (
      <Field name={field.name}>
        {({
          field: f,
          form: { setFieldValue, submitCount },
          meta,
        }: {
          field: FieldInputProps<string>
          form: FormikProps<FormValues>
          meta: { error?: string }
        }) => {
          const handleChange =
            (fieldName: string) => (value: string | null) => {
              setFieldValue(fieldName, value)
            }

          return (
            <Select
              value={f.value}
              options={field.options || []}
              isLoading={field.isLoading}
              error={submitCount > 0 && !!meta.error}
              disabled={field.disabled}
              onChange={handleChange(field.name)}
              onFocus={field.onFocus}
            />
          )
        }}
      </Field>
    ),

    date: (field) => (
      <Field name={field.name}>
        {({
          field: f,
          form: { setFieldValue, submitCount },
          meta,
        }: {
          field: FieldInputProps<string>
          form: FormikProps<FormValues>
          meta: { error?: string }
        }) => {
          const handleDateChange = (date: Date | null) => {
            setFieldValue(field.name, date ? date.toISOString() : null)
          }

          const dateValue = f.value ? new Date(f.value).toISOString() : null

          const handleDateFieldChange = (value: string | null) => {
            handleDateChange(value ? new Date(value) : null)
          }

          return (
            <DateField
              value={dateValue}
              error={submitCount > 0 && !!meta.error}
              disabled={field.disabled}
              onChange={handleDateFieldChange}
            />
          )
        }}
      </Field>
    ),

    checkbox: (field) => (
      <Field name={field.name} type="checkbox">
        {({ field: f }: { field: FieldInputProps<boolean> }) => (
          <div className="flex items-center">
            <input
              type="checkbox"
              id={field.name}
              {...f}
              checked={f.value}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor={field.name} className="ml-2 text-sm text-gray-700">
              {field.label}
            </label>
          </div>
        )}
      </Field>
    ),
  }

  const renderField = (field: FieldConfig, errors: FormikErrors<FormValues>) =>
    fieldRenderers[field.type]?.(field, errors) || <Field name={field.name} />

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <Formik
          initialValues={initialFormValues}
          validationSchema={() =>
            initialValues ? validationSchema(isEditMode) : undefined
          }
          validateOnChange={true}
          validateOnBlur={false}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, errors }) => (
            <FormikForm className="space-y-4">
              <div className="relative mb-6">
                <h2 className="text-lg font-semibold text-center">
                  {title || 'Form Title'}
                </h2>
                {onClose && (
                  <button
                    type="button"
                    className="absolute right-0 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                    onClick={onClose}
                  >
                    <CloseIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              {fields.map((field) => (
                <div key={field.name} className="mb-4">
                  <label
                    htmlFor={field.name}
                    className="block text-lg font-medium text-gray-700 mb-2 text-left"
                  >
                    {field.label}
                  </label>
                  {renderField(field, errors)}
                  <ErrorMessage
                    name={field.name}
                    component="div"
                    className="text-red-500 text-xs mt-1"
                  />
                </div>
              ))}

              <FormActions onSubmit={handleSubmit} onCancel={onCancel} />
            </FormikForm>
          )}
        </Formik>
      </div>
    </div>
  )
}

export default memo(Form)
